import {
  Box,
  Button,
  Container,
  Flex,
  FormLabel,
  Heading,
  HStack,
  Input,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { routes } from '../../pages/routes'
import { useLogout } from '../../hooks/useLogout'
import { useDispatch } from 'react-redux'
import { useAppSelector } from '../../app/typedReduxHooks'
import { UserType, selectRole } from '../onboardingContext/OnboardingContextSlice'
import { Role as AdminRole } from '../../types/Role'
import { useSSOClientConfig } from '../sso'
import useApi from '../../common/utils/api/useApi'
import { isEmailLike } from '../../utils/validation-helpers/emailFormatValidate'
import {
  OrganizationJoinPreview,
  OrgMembershipApiClient,
  sendJoinOrganizationRequest,
} from '../organization-membership/OrgMembershipSlice'

type Step = 'select-role' | 'confirm-join-request'

export const SelectRolesPage = () => {
  const [step, setStep] = useState<Step>('select-role')
  const [selectedRole, setSelectedRole] = useState<UserType | AdminRole | ''>('')
  const [orgPreview, setOrgPreview] = useState<OrganizationJoinPreview | null>(null)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const navigate = useNavigate()
  const toast = useToast()
  const logout = useLogout()
  const dispatch = useDispatch()
  const { authentication } = useAppSelector((state) => state)
  const {
    config: ssoClientConfig,
    hasFixedOrganization: isClientHasFixedOrganization,
    isPrescriberOnly: isClientPrescriberOnly,
    requiresJoinRequestApproval,
  } = useSSOClientConfig()

  useEffect(() => {
    if (isClientPrescriberOnly && !requiresJoinRequestApproval) {
      navigate(routes.createPrescriberProfile.path)
    }
  }, [isClientPrescriberOnly, requiresJoinRequestApproval])

  // The SSO token's given/family name isn't always reliable (some IdPs don't
  // map it, or fall back to the email address) - prefill as a convenience
  // but always let the user confirm/correct it before we send the request.
  useEffect(() => {
    if (authentication.firstName && !isEmailLike(authentication.firstName)) {
      setFirstName(authentication.firstName)
    }
    if (authentication.familyName && !isEmailLike(authentication.familyName)) {
      setLastName(authentication.familyName)
    }
  }, [authentication.firstName, authentication.familyName])

  const { trigger: triggerGetOrgPreview, loading: orgPreviewLoading } = useApi(
    OrgMembershipApiClient.getOrganizationJoinPreview,
    {
      onSuccess: (preview) => {
        setOrgPreview(preview)
        setStep('confirm-join-request')
      },
      onFailure: (error) => {
        toast({
          title:
            error.response?.data?.message ??
            'Failed to load your organisation details. Please try again.',
          status: 'error',
          duration: 5000,
        })
      },
    }
  )

  const { trigger: triggerJoinFixedOrganization, loading: joinFixedOrganizationLoading } = useApi(
    sendJoinOrganizationRequest,
    {
      onSuccess: () => {
        navigate(routes.joinOrganizationPending.path)
      },
      onFailure: (error) => {
        toast({
          title:
            error.response?.data?.message ??
            'Failed to submit your join request. Please try again.',
          status: 'error',
          duration: 5000,
        })
      },
    }
  )

  const handleRoleSubmit = () => {
    if (!selectedRole) {
      toast({
        title: 'Error',
        description: 'Please select a role to continue',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    dispatch(selectRole(selectedRole))
    if (isClientHasFixedOrganization && requiresJoinRequestApproval) {
      const organizationId = ssoClientConfig?.orgId
      if (!organizationId) {
        toast({
          title: 'Error',
          description: 'Organisation configuration is missing. Please contact support.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return
      }
      // Show the user which organisation they're about to request to join
      // before actually submitting the request, since they can't tell from
      // the login flow alone which of the orgs sharing this SSO connector
      // they'll be linked to.
      triggerGetOrgPreview(organizationId)
      return
    }
    if (isClientHasFixedOrganization) {
      if (selectedRole === UserType.PRESCRIBER) {
        navigate(routes.createPrescriberProfile.path)
      } else {
        navigate(routes.createOrganizationAdminProfile.path)
      }
    } else {
      navigate(routes.linkOrganization.path)
    }
  }

  const handleConfirmSendJoinRequest = () => {
    const organizationId = ssoClientConfig?.orgId
    if (!organizationId || !selectedRole) return
    if (!firstName.trim() || !lastName.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter your first and last name to continue',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }
    triggerJoinFixedOrganization({
      organizationId,
      requestedRole: selectedRole === UserType.PRESCRIBER ? UserType.PRESCRIBER : UserType.ADMIN,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
    })
  }

  if (step === 'confirm-join-request') {
    return (
      <Container maxW="container.sm" py={24}>
        <Box borderWidth="1px" borderRadius="lg" p={8} boxShadow="lg" bg="white">
          <Flex direction="column" align="center" gap={6}>
            <Heading size="lg" textAlign="center">
              Confirm Your Organisation
            </Heading>
            <Text color="gray.600" textAlign="center">
              Please confirm this is the correct organisation before sending your join request. An
              administrator of this organisation will need to approve your request before you can
              access RxPad.
            </Text>

            <VStack
              width="100%"
              spacing={3}
              align="stretch"
              p={4}
              borderRadius="md"
              bg="gray.50"
              borderWidth="1px"
              borderColor="gray.200"
            >
              <HStack justifyContent="space-between">
                <Text fontWeight="bold">Organisation:</Text>
                <Text>{orgPreview?.name}</Text>
              </HStack>
              <HStack justifyContent="space-between">
                <Text fontWeight="bold">Requested Role:</Text>
                <Text>{selectedRole}</Text>
              </HStack>
            </VStack>

            <VStack width="100%" spacing={4} align="stretch">
              <Box>
                <FormLabel mb={1} htmlFor="join-request-first-name">
                  First Name
                </FormLabel>
                <Input
                  id="join-request-first-name"
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </Box>
              <Box>
                <FormLabel mb={1} htmlFor="join-request-last-name">
                  Last Name
                </FormLabel>
                <Input
                  id="join-request-last-name"
                  placeholder="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </Box>
            </VStack>

            <Button
              colorScheme="blue"
              size="lg"
              width="full"
              onClick={handleConfirmSendJoinRequest}
              isLoading={joinFixedOrganizationLoading}
            >
              Send Join Request
            </Button>
            <Link
              as="button"
              type="button"
              onClick={() => setStep('select-role')}
              color="blue.500"
              textAlign="center"
              fontSize={'sm'}
              _hover={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              Back
            </Link>
          </Flex>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxW="container.sm" py={24}>
      <Box borderWidth="1px" borderRadius="lg" p={8} boxShadow="lg" bg="white">
        <Flex direction="column" align="center" gap={6}>
          <Heading size="lg" textAlign="center">
            Select a Role to Create User
          </Heading>

          <Text color="gray.600" textAlign="center">
            Please select a role before creating your user profile. This will determine your access
            permissions and features.
          </Text>

          {isClientHasFixedOrganization && requiresJoinRequestApproval && (
            <Text color="gray.600" fontSize="sm" textAlign="center">
              Your organisation requires approval before you can access RxPad. After you continue,
              an administrator of your organisation will need to approve your request.
            </Text>
          )}

          <RadioGroup
            onChange={(value) => setSelectedRole(value as UserType | AdminRole)}
            value={selectedRole}
          >
            <Stack spacing={5} width="full">
              <Radio value={UserType.PRESCRIBER} size="lg" borderColor="gray.300" spacing={4}>
                <Box>
                  <Text fontWeight="semibold">Prescriber</Text>
                  <Text fontSize="sm" color="gray.600">
                    Healthcare professionals who can prescribe medications
                  </Text>
                </Box>
              </Radio>

              {isClientHasFixedOrganization && !requiresJoinRequestApproval ? (
                <VStack spacing={4} align="flex-start">
                  <Box position="relative" w="full" display="flex" alignItems="center">
                    <Box flex="1" borderTop="1px solid" borderColor="gray.200" />
                    <Text px={3} fontSize="sm" color="gray.500" bg="white" fontWeight="medium">
                      Non-Prescriber
                    </Text>
                    <Box flex="1" borderTop="1px solid" borderColor="gray.200" />
                  </Box>
                  <Radio
                    value={AdminRole.PATIENT_ADMIN}
                    size="lg"
                    borderColor="gray.300"
                    spacing={4}
                  >
                    <Box>
                      <Text fontWeight="semibold">Patient Admin</Text>
                      <Text fontSize="sm" color="gray.600">
                        Manage patient data (non-prescribing role)
                      </Text>
                    </Box>
                  </Radio>

                  <Radio value={AdminRole.RX_ADMIN} size="lg" borderColor="gray.300" spacing={4}>
                    <Box>
                      <Text fontWeight="semibold">RxAdmin</Text>
                      <Text fontSize="sm" color="gray.600">
                        Manage patients and prescription data (non-prescribing role)
                      </Text>
                    </Box>
                  </Radio>
                </VStack>
              ) : (
                <Radio value={UserType.ADMIN} size="lg" borderColor="gray.300" spacing={4}>
                  <Box>
                    <Text fontWeight="semibold">Non-Prescriber</Text>
                    <Text fontSize="sm" color="gray.600">
                      Healthcare professionals who do not prescribe medications
                    </Text>
                  </Box>
                </Radio>
              )}
            </Stack>
          </RadioGroup>

          <Button
            colorScheme="blue"
            size="lg"
            width="full"
            onClick={handleRoleSubmit}
            isLoading={orgPreviewLoading}
            mt={8}
          >
            Continue
          </Button>
          <Link
            onClick={(e) => {
              e.preventDefault()
              navigate('/login')
              logout()
            }}
            color="blue.500"
            textAlign="center"
            fontSize={'sm'}
            _hover={{ textDecoration: 'underline', cursor: 'pointer' }}
          >
            Back to Login Page
          </Link>
        </Flex>
      </Box>
    </Container>
  )
}
